import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Returns a ref and a boolean `isVisible`.
 * Once the element scrolls into view, `isVisible` flips to
 * `true` and stays true (no re-hiding).
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 50 && rect.bottom > 0) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    // Check immediately and after a frame (for post-mount layout)
    check();
    const raf = requestAnimationFrame(check);

    const onScroll = () => {
      if (!ref.current) return;
      check();
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [check]);

  // Stop listening once visible
  useEffect(() => {
    if (isVisible) {
      // No cleanup needed — the scroll listener will short-circuit
    }
  }, [isVisible]);

  return { ref, isVisible };
}
